import { 
    Checkbox, 
    FormControlLabel, 
    Grid, 
    Select, 
    Tooltip, 
    Typography, 
    Box,
    MenuItem,
    FormControl,
    Card,
    TextField
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";

const SettingCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
}));

const SettingItem = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
        borderBottom: 'none',
    },
}));

const GridContainer = styled(Grid)(({ theme }) => ({
    width: '100%',
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 200,
    marginLeft: 'auto',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
    '&:first-of-type': {
        marginTop: 0,
    },
}));

const ComponentSettings = ({ settings, getSettingValue, handleChangeBooleanSetting, handleChangeSetting }) => {
    const { t } = useTranslation();
    const [queues, setQueues] = useState([]);

    useEffect(() => {
        api.get("/queue").then(({ data }) => setQueues(Array.isArray(data) ? data : [])).catch(() => {});
    }, []);

    const groupsVisibility = getSettingValue("groupsVisibility") || "admins";

    const booleanSettings = [
        { key: "allTicket", label: t("settings.general.allTicket.name"), note: t("settings.general.allTicket.note") },
        { key: "CheckMsgIsGroup", label: t("settings.general.CheckMsgIsGroup.name"), note: t("settings.general.CheckMsgIsGroup.note") },
        { key: "call", label: t("settings.general.call.name"), note: t("settings.general.call.note") },
        { key: "sideMenu", label: t("settings.general.sideMenu.name"), note: t("settings.general.sideMenu.note") },
        { key: "quickAnswer", label: t("settings.general.quickAnswer.name"), note: t("settings.general.quickAnswer.note") },
        { key: "closeTicketApi", label: t("settings.general.closeTicketApi.name"), note: t("settings.general.closeTicketApi.note") },
        { key: "ASC", label: t("settings.general.ASC.name"), note: t("settings.general.ASC.note") },
        { key: "created", label: t("settings.general.created.name"), note: t("settings.general.created.note") },
        { key: "openTickets", label: t("settings.general.openTickets.name"), note: t("settings.general.openTickets.note") },
        { key: "signOption", label: t("settings.general.signOption.name"), note: t("settings.general.signOption.note") },
        { key: "tabsPending", label: t("settings.general.tabsPending.name"), note: t("settings.general.tabsPending.note") },
        { key: "tabsClosed", label: t("settings.general.tabsClosed.name"), note: t("settings.general.tabsClosed.note") },
        { key: "listItemSpy", label: t("settings.general.listItemSpy.name"), note: t("settings.general.listItemSpy.note") },
        { key: "queueLength", label: t("settings.general.queueLength.name"), note: t("settings.general.queueLength.note") },
    ];

    const settingsChunks = [];
    for (let i = 0; i < booleanSettings.length; i += 8) {
        settingsChunks.push(booleanSettings.slice(i, i + 8));
    }

    const groupedSettings = [
        {
            title: t("settings.general.systemBehavior"),
            settings: booleanSettings.filter(s => [
                'userCreation'
            ].includes(s.key))
        },
        {
            title: t("settings.general.userInterface"),
            settings: booleanSettings.filter(s => [
                'quickAnswer', 'signOption', 'listItemSpy', 'tabsPending', 'tabsClosed',
            ].includes(s.key))
        },
        {
            title: t("settings.general.ticketManagement"),
            settings: booleanSettings.filter(s => [
                'allTicket', 'CheckMsgIsGroup', 'closeTicketApi', 'ASC', 'created', 'openTickets', 
                'queueLength'
            ].includes(s.key))
        }
    ];

    return (
        <GridContainer container spacing={2}>
            {groupedSettings.filter(group => group.settings.length > 0).map((group, groupIndex) => (
                <Grid item xs={12} key={groupIndex}>
                    <SectionTitle variant="h6">{group.title}</SectionTitle>
                    <SettingCard>
                        {group.settings.map((setting, idx) => (
                            <SettingItem key={setting.key}>
                                <Tooltip title={setting.note} placement="top-start">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={settings && settings.length > 0 && getSettingValue(setting.key) === "enabled"}
                                                onChange={handleChangeBooleanSetting}
                                                name={setting.key}
                                                color="primary"
                                            />
                                        }
                                        label={setting.label}
                                        sx={{ marginRight: 'auto' }}
                                    />
                                </Tooltip>
                            </SettingItem>
                        ))}
                    </SettingCard>
                </Grid>
            ))}
            
            {/* Grupos de WhatsApp (14/10/2026, Rio Brás tkt 60318) — ver helpers/TicketAccess.ts */}
            <Grid item xs={12}>
                <SectionTitle variant="h6">Grupos de WhatsApp</SectionTitle>
                <SettingCard>
                    <SettingItem>
                        <Tooltip title="Quem enxerga os grupos de WhatsApp em que o número está. Com 'Todos os atendentes' ou 'Um setor', qualquer um responde sem aceitar, o grupo não toca som e só admin finaliza." placement="top-start">
                            <Typography variant="body1">Grupos visíveis para</Typography>
                        </Tooltip>
                        <StyledFormControl variant="outlined" size="small">
                            <Select
                                id="groupsVisibility-setting"
                                name="groupsVisibility"
                                value={groupsVisibility}
                                onChange={handleChangeSetting}
                            >
                                <MenuItem value="admins">Só administradores</MenuItem>
                                <MenuItem value="all">Todos os atendentes</MenuItem>
                                <MenuItem value="queue">Um setor</MenuItem>
                            </Select>
                        </StyledFormControl>
                    </SettingItem>
                    {groupsVisibility === "queue" && (
                        <SettingItem>
                            <Typography variant="body1">Setor que vê os grupos</Typography>
                            <StyledFormControl variant="outlined" size="small">
                                <Select
                                    id="groupsQueueId-setting"
                                    name="groupsQueueId"
                                    value={getSettingValue("groupsQueueId") || ""}
                                    onChange={handleChangeSetting}
                                    displayEmpty
                                >
                                    <MenuItem value="" disabled>Escolha o setor</MenuItem>
                                    {queues.map((q) => (
                                        <MenuItem key={q.id} value={String(q.id)}>{q.name}</MenuItem>
                                    ))}
                                </Select>
                            </StyledFormControl>
                        </SettingItem>
                    )}
                </SettingCard>
            </Grid>

            {/* Limite por atendente + confirmação ao finalizar (17/10/2026, pedidos da Insetisan) */}
            <Grid item xs={12}>
                <SectionTitle variant="h6">Atendimento</SectionTitle>
                <SettingCard>
                    <SettingItem>
                        <Tooltip title="Ao atingir o limite, o atendente não aceita nem abre novos tickets até finalizar algum. Não vale para administradores nem para grupos, e nunca impede a chegada de mensagens de clientes." placement="top-start">
                            <Typography variant="body1">Limite de atendimentos abertos por atendente</Typography>
                        </Tooltip>
                        <StyledFormControl variant="outlined" size="small">
                            <Select
                                id="userTicketLimit-setting"
                                name="userTicketLimit"
                                value={getSettingValue("userTicketLimit") || "0"}
                                onChange={handleChangeSetting}
                            >
                                <MenuItem value="0">Sem limite</MenuItem>
                                {["5", "10", "15", "20", "25", "30", "40", "50", "60", "80", "100"].map((n) => (
                                    <MenuItem key={n} value={n}>{n} atendimentos</MenuItem>
                                ))}
                            </Select>
                        </StyledFormControl>
                    </SettingItem>
                    <SettingItem>
                        <Tooltip title="Mostra 'Finalizar este atendimento?' antes de finalizar, para evitar finalizar sem querer." placement="top-start">
                            <Typography variant="body1">Pedir confirmação ao finalizar</Typography>
                        </Tooltip>
                        <StyledFormControl variant="outlined" size="small">
                            <Select
                                id="confirmCloseTicket-setting"
                                name="confirmCloseTicket"
                                value={getSettingValue("confirmCloseTicket") === "enabled" ? "enabled" : "disabled"}
                                onChange={handleChangeSetting}
                            >
                                <MenuItem value="disabled">Desligado</MenuItem>
                                <MenuItem value="enabled">Ligado</MenuItem>
                            </Select>
                        </StyledFormControl>
                    </SettingItem>
                </SettingCard>
            </Grid>

            {/* Carteira de clientes (15/10/2026, Rio Brás tkt 60318) — ver backend CarteiraService.ts */}
            <Grid item xs={12}>
                <SectionTitle variant="h6">Carteira de clientes</SectionTitle>
                <SettingCard>
                    <SettingItem>
                        <Tooltip title="Cliente que volta e escolhe um setor vai direto para o último atendente que o atendeu naquele setor. Se ele não responder no tempo abaixo (contado só no horário de expediente do setor), a conversa volta para o Aguardando e qualquer um pega." placement="top-start">
                            <Typography variant="body1">Cliente volta para o último atendente</Typography>
                        </Tooltip>
                        <StyledFormControl variant="outlined" size="small">
                            <Select
                                id="carteiraClientes-setting"
                                name="carteiraClientes"
                                value={getSettingValue("carteiraClientes") === "enabled" ? "enabled" : "disabled"}
                                onChange={handleChangeSetting}
                            >
                                <MenuItem value="disabled">Desligado</MenuItem>
                                <MenuItem value="enabled">Ligado</MenuItem>
                            </Select>
                        </StyledFormControl>
                    </SettingItem>
                    {getSettingValue("carteiraClientes") === "enabled" && (
                        <SettingItem>
                            <Typography variant="body1">Sem resposta do atendente, volta para o setor em</Typography>
                            <StyledFormControl variant="outlined" size="small">
                                <Select
                                    id="carteiraMinutos-setting"
                                    name="carteiraMinutos"
                                    value={getSettingValue("carteiraMinutos") || "15"}
                                    onChange={handleChangeSetting}
                                >
                                    {["5", "10", "15", "20", "30", "60"].map((m) => (
                                        <MenuItem key={m} value={m}>{m} minutos</MenuItem>
                                    ))}
                                </Select>
                            </StyledFormControl>
                        </SettingItem>
                    )}
                </SettingCard>
            </Grid>

            <Grid item xs={12}>
                <SectionTitle variant="h6">{t("settings.general.callSettings")}</SectionTitle>
                <SettingCard>
                    {(getSettingValue("call") !== "enabled" || getSettingValue("autoRejectCalls") === "enabled") && (
                        <SettingItem>
                            <Tooltip title={t("settings.general.autoRejectCalls.note")} placement="top-start">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={settings && settings.length > 0 && getSettingValue("autoRejectCalls") === "enabled"}
                                            onChange={handleChangeBooleanSetting}
                                            name="autoRejectCalls"
                                            color="primary"
                                        />
                                    }
                                    label={t("settings.general.autoRejectCalls.name")}
                                    sx={{ marginRight: 'auto' }}
                                />
                            </Tooltip>
                        </SettingItem>
                    )}
                    {getSettingValue("autoRejectCalls") === "enabled" && (
                        <SettingItem>
                            <Box sx={{ width: '100%' }}>
                                <Tooltip title={t("settings.general.autoRejectCallsMessage.note")} placement="top-start">
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        {t("settings.general.autoRejectCallsMessage.name")}
                                    </Typography>
                                </Tooltip>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    name="autoRejectCallsMessage"
                                    value={settings && settings.length > 0 ? getSettingValue("autoRejectCallsMessage") : ""}
                                    onChange={handleChangeSetting}
                                    placeholder={t("settings.general.autoRejectCallsMessage.placeholder")}
                                />
                            </Box>
                        </SettingItem>
                    )}
                    {(getSettingValue("autoRejectCalls") !== "enabled" || getSettingValue("call") === "enabled") && (
                        <SettingItem>
                            <Tooltip title={t("settings.general.call.note")} placement="top-start">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={settings && settings.length > 0 && getSettingValue("call") === "enabled"}
                                            onChange={handleChangeBooleanSetting}
                                            name="call"
                                            color="primary"
                                        />
                                    }
                                    label={t("settings.general.call.name")}
                                    sx={{ marginRight: 'auto' }}
                                />
                            </Tooltip>
                        </SettingItem>
                    )}
                </SettingCard>
            </Grid>

            <Grid item xs={12}>
                <SectionTitle variant="h6">{t("settings.general.timeSettings")}</SectionTitle>
                <SettingCard>
                    <SettingItem>
                        <Tooltip title={t("settings.general.timeCreateNewTicket.note")} placement="top-start">
                            <Typography variant="body1">
                                {t("settings.general.timeCreateNewTicket.name")}
                            </Typography>
                        </Tooltip>
                        <StyledFormControl variant="outlined" size="small">
                            <Select
                                id="timeCreateNewTicket-setting"
                                name="timeCreateNewTicket"
                                value={settings && settings.length > 0 ? getSettingValue("timeCreateNewTicket") : ""}
                                onChange={handleChangeSetting}
                            >
                                <MenuItem value="10">{t("settings.general.timeCreateNewTicket.options.10")}</MenuItem>
                                <MenuItem value="30">{t("settings.general.timeCreateNewTicket.options.30")}</MenuItem>
                                <MenuItem value="60">{t("settings.general.timeCreateNewTicket.options.60")}</MenuItem>
                                <MenuItem value="300">{t("settings.general.timeCreateNewTicket.options.300")}</MenuItem>
                                <MenuItem value="1800">{t("settings.general.timeCreateNewTicket.options.1800")}</MenuItem>
                                <MenuItem value="3600">{t("settings.general.timeCreateNewTicket.options.3600")}</MenuItem>
                                <MenuItem value="7200">{t("settings.general.timeCreateNewTicket.options.7200")}</MenuItem>
                                <MenuItem value="21600">{t("settings.general.timeCreateNewTicket.options.21600")}</MenuItem>
                                <MenuItem value="43200">{t("settings.general.timeCreateNewTicket.options.43200")}</MenuItem>
                                <MenuItem value="86400">{t("settings.general.timeCreateNewTicket.options.86400")}</MenuItem>
                                <MenuItem value="604800">{t("settings.general.timeCreateNewTicket.options.604800")}</MenuItem>
                                <MenuItem value="1296000">{t("settings.general.timeCreateNewTicket.options.1296000")}</MenuItem>
                                <MenuItem value="2592000">{t("settings.general.timeCreateNewTicket.options.2592000")}</MenuItem>
                            </Select>
                        </StyledFormControl>
                    </SettingItem>
                    <SettingItem>
                        <Tooltip title={t("settings.general.sessionTimeout.note")} placement="top-start">
                            <Typography variant="body1">
                                {t("settings.general.sessionTimeout.name")}
                            </Typography>
                        </Tooltip>
                        <StyledFormControl variant="outlined" size="small">
                            <Select
                                id="sessionTimeout-setting"
                                name="sessionTimeout"
                                value={settings && settings.length > 0 ? (getSettingValue("sessionTimeout") || "8") : "8"}
                                onChange={handleChangeSetting}
                            >
                                <MenuItem value="1">{t("settings.general.sessionTimeout.options.1")}</MenuItem>
                                <MenuItem value="2">{t("settings.general.sessionTimeout.options.2")}</MenuItem>
                                <MenuItem value="4">{t("settings.general.sessionTimeout.options.4")}</MenuItem>
                                <MenuItem value="6">{t("settings.general.sessionTimeout.options.6")}</MenuItem>
                                <MenuItem value="8">{t("settings.general.sessionTimeout.options.8")}</MenuItem>
                                <MenuItem value="12">{t("settings.general.sessionTimeout.options.12")}</MenuItem>
                                <MenuItem value="24">{t("settings.general.sessionTimeout.options.24")}</MenuItem>
                                <MenuItem value="48">{t("settings.general.sessionTimeout.options.48")}</MenuItem>
                                <MenuItem value="72">{t("settings.general.sessionTimeout.options.72")}</MenuItem>
                                <MenuItem value="168">{t("settings.general.sessionTimeout.options.168")}</MenuItem>
                            </Select>
                        </StyledFormControl>
                    </SettingItem>
                </SettingCard>
            </Grid>

            <Grid item xs={12}>
                <SectionTitle variant="h6">Configurações de Email</SectionTitle>
                <SettingCard>
                    <SettingItem>
                        <Box sx={{ width: '100%' }}>
                            <Tooltip title="Endereço de email usado para envio de notificações" placement="top-start">
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    Email do Remetente
                                </Typography>
                            </Tooltip>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="emailUser"
                                value={settings && settings.length > 0 ? getSettingValue("emailUser") : ""}
                                onChange={handleChangeSetting}
                                placeholder="seu-email@gmail.com"
                                type="email"
                            />
                        </Box>
                    </SettingItem>
                    <SettingItem>
                        <Box sx={{ width: '100%' }}>
                            <Tooltip title="Senha de aplicativo do Gmail (não use sua senha normal)" placement="top-start">
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    Senha de Aplicativo
                                </Typography>
                            </Tooltip>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="emailPass"
                                value={settings && settings.length > 0 ? getSettingValue("emailPass") : ""}
                                onChange={handleChangeSetting}
                                placeholder="xxxx xxxx xxxx xxxx"
                                type="password"
                            />
                        </Box>
                    </SettingItem>
                    <SettingItem>
                        <Box sx={{ width: '100%' }}>
                            <Tooltip title="Servidor SMTP (padrão: smtp.gmail.com)" placement="top-start">
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    Servidor SMTP
                                </Typography>
                            </Tooltip>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="emailHost"
                                value={settings && settings.length > 0 ? getSettingValue("emailHost") : "smtp.gmail.com"}
                                onChange={handleChangeSetting}
                                placeholder="smtp.gmail.com"
                            />
                        </Box>
                    </SettingItem>
                    <SettingItem>
                        <Box sx={{ width: '100%' }}>
                            <Tooltip title="Porta do servidor SMTP (padrão: 587)" placement="top-start">
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    Porta SMTP
                                </Typography>
                            </Tooltip>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                name="emailPort"
                                value={settings && settings.length > 0 ? getSettingValue("emailPort") : "587"}
                                onChange={handleChangeSetting}
                                placeholder="587"
                                type="number"
                            />
                        </Box>
                    </SettingItem>
                </SettingCard>
            </Grid>

            <Grid item xs={12}>
                <SectionTitle variant="h6">Janela de atendimento (WhatsApp Oficial)</SectionTitle>
                <SettingCard>
                    <SettingItem>
                        <Tooltip
                            title="Mostra no cabeçalho do ticket se a janela de 24h da API oficial está aberta, prestes a fechar ou fechada. Aparece apenas em conexões oficiais (WABA)."
                            placement="top-start"
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            !settings ||
                                            settings.length === 0 ||
                                            getSettingValue("waba24hWindowEnabled") !== "disabled"
                                        }
                                        onChange={handleChangeBooleanSetting}
                                        name="waba24hWindowEnabled"
                                        color="primary"
                                    />
                                }
                                label="Exibir indicador da janela de 24h"
                                sx={{ marginRight: 'auto' }}
                            />
                        </Tooltip>
                    </SettingItem>
                    <SettingItem>
                        <Box sx={{ width: '100%' }}>
                            <Tooltip
                                title="Quantas horas antes do fechamento o indicador deve mudar para o estado de alerta (laranja). Padrão: 3 horas."
                                placement="top-start"
                            >
                                <Typography variant="body1" sx={{ mb: 1 }}>
                                    Antecedência do alerta (horas)
                                </Typography>
                            </Tooltip>
                            <TextField
                                variant="outlined"
                                size="small"
                                name="waba24hWindowAlertHours"
                                value={settings && settings.length > 0 ? (getSettingValue("waba24hWindowAlertHours") || "3") : "3"}
                                onChange={handleChangeSetting}
                                type="number"
                                inputProps={{ min: 1, max: 23 }}
                                sx={{ width: 120 }}
                            />
                        </Box>
                    </SettingItem>
                </SettingCard>
            </Grid>
        </GridContainer>
    );
};

export default ComponentSettings;
